/**
 * Centralized Feature Flag Configuration
 *
 * This file defines all feature flags used in the application.
 * It provides a single source of truth for feature flag definitions,
 * making it easy to manage, document, and use feature flags consistently.
 *
 * All feature flags support org-specific targeting via GrowthBook,
 * with automatic fallback to environment-wide when no org rules are defined.
 */

// ============================================================================
// FEATURE FLAG DEFINITIONS
// ============================================================================

export const FEATURE_FLAGS = {
  UI_TEMP_ENABLE_CRED_FILTER: {
    key: 'ui:temp-enable-cred-filter',
    defaultValue: false,
    description:
      'Temporary feedback rollout for the connector credential filter. Remove within one release after enabling for everyone. Credential counts remain visible independently.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // Python 3 Support
  UI_ENABLE_PYTHON3: {
    key: 'ui:enable-python3',
    defaultValue: true,
    description:
      'Enable Python 3 support. Can be targeted to specific organizations via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // Splitter Component
  UI_ENABLE_SPLITTER: {
    key: 'ui:enable-splitter',
    defaultValue: true,
    description:
      'Enable splitter component. Can be targeted to specific organizations via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // Flow Run Now Node
  UI_DISPLAY_FLOW_RUN_NOW: {
    key: 'ui:display-flow-run-now',
    defaultValue: false,
    description:
      'Display the flow run now node above the source in canvas. Can be targeted to specific organizations via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // DAG Modals - JSON configuration for modal capabilities
  UI_DAG_MODALS: {
    key: 'ui:dag-modals',
    defaultValue: {
      credentials: true,
      source: {
        replication: true,
        spark: true,
        cdc: true,
        in_memory: true,
        elt: true,
        custom: true,
        streaming: true,
      },
      sink: {
        replication: true,
        spark: true,
        cdc: true,
        in_memory: true,
        elt: true,
        custom: true,
        streaming: true,
      },
      nexset: true,
    },
    description:
      'Granular control of DAG modal capabilities. Migrates REACT_APP_EDIT_MODALS_ENABLED with org-specific targeting via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // API Server Flow
  UI_ENABLE_API_SERVER_FLOW: {
    key: 'ui:enable-api-server',
    defaultValue: false,
    description:
      'Enable API Server flow type in the flow type selection list. Can be targeted to specific organizations via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // CDC v2
  UI_ENABLE_CDC_V2: {
    key: 'ui:enable-cdc-v2',
    defaultValue: false,
    description: '-',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // CDC v2 filters
  UI_ENABLE_CDC_V2_FILTERS: {
    key: 'ui:enable-cdc-v2-filters',
    defaultValue: false,
    description: '-',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // CDC Envelope v2
  UI_CDC_ENVELOPE_V2: {
    key: 'ui:cdc_envelope_v2',
    defaultValue: false,
    description:
      'Use the unified CDCv2 envelope spec for validation instead of per-flow-type v1 specs. Applies to both CDC and ELT flows.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // Debug Console Logs
  UI_ENABLE_DEBUGGER: {
    key: 'ui:enable-debugger',
    defaultValue: {
      enabled: false,
      debug_level: 0,
      block_api_calls: false,
    },
    description:
      'Control debug console logging. enabled: show debug logs. debug_level: verbosity (0-3). block_api_calls: when true, logs payload but prevents API call from firing.',
    type: 'feature' as const,
    category: 'development' as const,
  },

  // Organization Config Overrides
  UI_ORG_CONFIG_OVERRIDES: {
    key: 'ui:org-config-overrides',
    defaultValue: {},
    description:
      'Organization-specific configuration overrides for source/sink creation payloads. Migrates REACT_APP_ORG_CONFIG_OVERRIDES with org-specific targeting via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  // Common connectors for new users onboarding
  UI_COMMON_CONNECTORS: {
    key: 'ui:common-connectors',
    defaultValue: {
      s3: {
        setupTime: '',
        description: 'Connect your S3 data and process files in any format',
      },
      sf_api: {
        setupTime: '',
        description: 'Connect your Salesforce data',
      },
      shopify_api: {
        setupTime: '',
        description: 'Connect your Shopify store data',
      },
      nexla_rest: {
        setupTime: '',
        description: 'Receive incoming data from any webhook-capable application or service',
      },
      postgres: {
        setupTime: '',
        description: 'Connect your PostgreSQL database',
      },
      file_upload: {
        setupTime: '',
        description: 'Upload and process your files',
      },
    },
    description: 'Common connectors to highlight for new users',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  // Enable Preload Web Workers for Monaco
  UI_ENABLE_PRELOAD_WEB_WORKERS_FOR_MONACO: {
    key: 'ui:enable-preload-web_workers-for-monaco',
    defaultValue: true,
    description:
      'Enable preloading web workers for Monaco editor. Can be targeted to specific organizations via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  // Enable Flow Insights V2
  UI_ENABLE_FLOW_INSIGHTS_V2: {
    key: 'ui:enable-flow-insights-v2',
    defaultValue: true,
    description:
      'Enable flow insights v2. Can be targeted to specific organizations via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  UI_LIMITED_ACCESS: {
    key: 'ui:limited-access',
    defaultValue: {
      express_tiers: [],
      self_signup_tiers: [],
    },
    description:
      'Limit access to certain Nexla UI features for users based on their organizations tier.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  UI_CONNECTOR_CAPTURE_EARLY_INTENT: {
    key: 'ui:capture-early-intent',
    defaultValue: true,
    description:
      'Capture early intent for flow creation. in AGRESSIVE mode, the early intent is captured when the connector is selected. in NON-AGRESSIVE mode, the early intent is captured when the creditial are selected.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // Polling Configuration - Single source of truth for all polling intervals
  UI_POLLING_CONFIG: {
    key: 'ui:polling-config',
    defaultValue: {
      intervalMs: null as number | null, // null = use env variable (default: 10s), number = global override
      pauseOnHidden: true, // Pause polling when tab is hidden
      refreshOnVisible: true, // Immediate refresh when tab becomes visible
      pauseOnIdle: false, // Pause polling when user is idle
      idleTimeoutMs: 300000, // 5 minutes idle timeout
      enableAdaptiveBackoff: false, // Enable exponential backoff
      maxBackoffMultiplier: 4, // Max 4x the base interval (e.g., 10s -> 40s)
      // Preset-specific overrides (optional) - configure individual polling scenarios
      // Available presets: DEFAULT, FLOW_LOGS, LIVE_SAMPLES, PIPELINE_LIST, UI_TIME_DISPLAY, SCANNING
      presets: undefined as
        | {
            DEFAULT?: { intervalMs?: number };
            FLOW_LOGS?: { intervalMs?: number };
            LIVE_SAMPLES?: { intervalMs?: number };
            PIPELINE_LIST?: { intervalMs?: number };
            UI_TIME_DISPLAY?: { intervalMs?: number };
            SCANNING?: { intervalMs?: number };
          }
        | undefined,
    },
    description:
      'Centralized polling configuration with preset support. Global intervalMs overrides env variable. Use "presets" object to configure specific scenarios: { "presets": { "FLOW_LOGS": { "intervalMs": 15000 }, "PIPELINE_LIST": { "intervalMs": 45000 } } }. Defaults: FLOW_LOGS=10s, LIVE_SAMPLES=20s, PIPELINE_LIST=30s, SCANNING=5s.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // AI Main Menu Item
  UI_AI_MENU: {
    key: 'ui:ai-main-menu',
    defaultValue: false,
    description:
      'Enable the AI main menu item in the sidebar navigation. Can be targeted to specific organizations via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // MCP Flows Page
  UI_ENABLE_MCP_FLOWS: {
    key: 'ui:enable-mcp-flows',
    defaultValue: false,
    description:
      'Enable the MCP Flows page (/ai/mcp-flows) and its AI-menu nav item. Can be targeted to specific organizations via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // AI Triage Chat
  UI_AI_TRIAGE_CHAT: {
    key: 'ui:ai-triage-chat',
    defaultValue: false,
    description:
      'Enable the AI triage chat: an AI icon in Monitor → Runs rows and the /monitor/triage-chat deep-link route. Targetable per org via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // Marketplace Data Products as AI tools
  UI_AI_MARKETPLACE_DATA_PRODUCT_TOOLS: {
    key: 'ui:ai-marketplace-data-product-tools',
    defaultValue: false,
    description:
      'Show the Data Products segment in the AI tool picker, letting a user build tools from Marketplace-published Nexsets they can reach. Requires the MCPaaS Marketplace support (ai#987) to be deployed: without it tool creation from a granted Data Product is rejected. Targetable per org via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // Triage Express Backend
  UI_TRIAGE_EXPRESS_BACKEND: {
    key: 'ui:triage-express-backend',
    defaultValue: false,
    description:
      'Route Triage AI Chat to the express-code backend (SSE) instead of veda-ai (WebSocket). Requires UI_AI_TRIAGE_CHAT to also be on.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // FastAPI Migration
  UI_ENABLE_FAST_API: {
    key: 'ui:enable-fast-api',
    defaultValue: false,
    description:
      'Enable FastAPI endpoints for admin-api migration. When enabled, requests to FastAPI routes will use FastAPI server.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  // FastAPI Routes Configuration
  UI_FASTAPI_ROUTES: {
    key: 'ui:fastapi-routes',
    defaultValue: {},
    /*format: { '/flows':['GET'], '/users':['POST']} */
    description:
      'Configure which API routes should use FastAPI. Map of route paths to allowed HTTP methods. Only routes listed here will be routed to FastAPI when UI_ENABLE_FAST_API is enabled.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  // Multi-Block Transform JOLT Generation
  UI_ENABLE_MULTI_BLOCK_TRANSFORMS: {
    key: 'ui:enable-multi-block-transforms',
    defaultValue: false,
    description:
      'Enable multi-block JOLT generation for transform recipes. When enabled, supports multiple TRANSFORMS blocks, CODE blocks, and proper nexla.end_block separation. When disabled, uses legacy single-block mode for backward compatibility. Can be targeted to specific organizations via GrowthBook for gradual rollout.',
    type: 'feature' as const,
    category: 'experimental' as const,
  },
  // SQL Automation
  UI_ENABLE_SQL_AUTOMATION: {
    key: 'ui:tasks',
    defaultValue: false,
    description: 'Enable SQL automation. Can be targeted to specific organizations via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  UI_FILE_UPLOAD_TEST_SECTION_ENABLED: {
    key: 'ui:file-upload-test-section-enabled',
    defaultValue: false,
    description:
      'Controls visibility of File Upload test section in custom processor UI. When disabled, selected-file test section is hidden.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  UI_FILE_UPLOAD_PROCESSOR_WITH_PARSER_ENABLED: {
    key: 'ui:file-upload-processor-with-parser-enabled',
    defaultValue: false,
    description:
      'When enabled, allows custom processor and AI function to be used alongside a file parser in File Upload. When disabled, these options are only available with Skip File Parsing (Raw File Data).',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  // UI App Version Control
  UI_APP_VERSION: {
    key: 'ui:app-version',
    defaultValue: 'v1',
    description:
      'Controls UI version routing. v1=stable, v2=next. Org-targeted via GrowthBook for gradual rollout.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  // Enable Express Embedded
  UI_ENABLE_EXPRESS: {
    key: 'ui:enable-express',
    defaultValue: false,
    description:
      'Enable embedded experience for Express tier users. Can be targeted to specific organizations via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  UI_ENABLE_SDK_APPLICATIONS: {
    key: 'ui:enable-sdk-applications',
    defaultValue: false,
    description:
      'Enable SDK applications feature. Can be targeted to specific organizations via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // Capsules (Settings): list, detail, keys, registration
  UI_ENABLE_CAPSULES_SETTINGS: {
    key: 'ui:enable-capsules-settings',
    defaultValue: false,
    description:
      'Show Capsules under Organization settings and allow capsules list/detail routes. Targeted via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  // Runtime Selection
  UI_ENABLE_RUNTIME_SELECTION: {
    key: 'ui:enable-runtime-selection',
    description:
      'Show runtime selection field under organization settings. Targeted via GrowthBook.',
    defaultValue: {
      streaming: {
        rest: false,
        templatized: false,
        files: false,
        database: false,
      },
    },
    type: 'feature' as const,
    category: 'ui' as const,
  },
  // REST API source redesign
  // TODO(CLEANUP-REST-REDESIGN-FLAG): when the redesign hits 100% rollout, delete this
  // entry + every getFeatureFlagValue('UI_ENABLE_REST_API_SOURCE_REDESIGN') read + the
  // legacy ConfigureAPI/ConfigureTemplatizedAPI branches. Playbook: docs/testing/feature-flags-in-e2e.md
  UI_ENABLE_REST_API_SOURCE_REDESIGN: {
    // Matches the flag created in GrowthBook (no ui: prefix), targeted there per-org.
    key: 'rest-api-source-redesign',
    // Off by default: the redesign is rolled out per-org via GrowthBook targeting.
    // This fallback only applies where GrowthBook is not connected (e.g. some local/
    // headless envs); production/edit visibility is controlled entirely by GrowthBook.
    defaultValue: false,
    description:
      'Show the redesigned REST API source configuration screen (Advanced step list). The Template re-skin is always on (CSS-only). Targeted via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  // Capsule Helm Deployment Defaults
  UI_CAPSULE_HELM_DEFAULTS: {
    key: 'ui:capsule-helm-defaults',
    defaultValue: {
      version: '',
      httpGatewayEndpointFqdn: '',
      chartRepository: '',
      capsuleDockerImage: '',
      alloyTelemetryApiKey: '',
    },
    description:
      'Default values for capsule Helm deployment command fields. Non-Docker image fields with non-empty defaults are pre-populated and read-only in the Helm Command Preview form; missing defaults render blank and remain editable. capsuleDockerImage is command-only and must come from GrowthBook. The PVC Claim Name is always user-editable regardless of this flag.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  UI_ENABLE_UTILIZATION_FLOW_ACTIVITY: {
    key: 'ui:enable-utilization-flow-activity',
    defaultValue: false,
    description:
      'Display the Flow Activity (daily flow trends) chart on the Utilization page. Targetable per org via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  UI_ENABLE_FLOW_INSIGHTS_DAILY_METRICS: {
    key: 'ui:enable-flow-insights-daily-metrics',
    defaultValue: false,
    description:
      'Display the Recent Usage Trends daily-metrics chart and KPI cards in the flow Overview tab (flow detail drawer and Resource Triage). Targetable per org via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },

  // OAuth Server-Side Flow
  UI_OAUTH_SERVER_FLOW: {
    key: 'ui:oauth-server-flow',
    defaultValue: false,
    description:
      'When enabled, OAuth credential creation uses the server-side flow (POST /oauth/start) instead of the legacy client-side token exchange. Requires admin-api feat/oauth-server-flow.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  UI_ENABLE_BYO_TEST_LOGS: {
    key: 'ui:enable-byo-test-logs',
    defaultValue: false,
    description:
      'Show the Logs tab in the BYO code processor Test panel. Off by default until nexla_utils log output is actually wired through the probe response. Targetable per org via GrowthBook.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  // BYO Flow — Performance Config panel
  UI_BYO_PERFORMANCE_CONFIG: {
    key: 'ui:byo-performance-config',
    defaultValue: false,
    description:
      'Show the Performance settings panel in the BYO Flow code editor (CPU, memory, timeout, retries). Off by default until Ray resource wiring is complete.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  // BYO Flow — V2 (Studio/canvas shells) rollout control.
  UI_ENABLE_BYO_V2: {
    key: 'ui:enable-byo-v2',
    // Flipped false→ (was true 2026-08-19, reverted 2026-08-20): a default-on
    // "kill switch" ships every org already allow-listed via the legacy
    // org.features_enabled "byo" flag straight into the still-actively-
    // buggy V2 rewrite with zero opt-in — a 2026-08-20 QA pass found live
    // blockers in the exact create path this flag gates (an empty-code
    // container silently auto-created + activated on every canvas source
    // creation; a hydration race that could wipe an edited source's config).
    // Opt-in per org via GrowthBook once V2 is verified for them, then widen.
    defaultValue: false,
    description:
      'Gates the BYO V2 (Studio/canvas shell) create experience. Every prior BYO rollout was gated separately from the legacy org.features_enabled "byo" allowlist — V2 shipped with no dedicated flag of its own; this fills that gap. Defaults false — opt in per-org via GrowthBook once V2 is verified for them; flip the whole flag once it is ready for general availability.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
  // BYO Flow — automatic package detection from code imports.
  UI_BYO_AUTO_PACKAGE_DETECTION: {
    key: 'ui:byo-auto-package-detection',
    // Requested 2026-08-20 (product review): auto-adding a package the
    // moment its import appears in the code is a UI opinion about what an
    // existing pipeline needs, silently modifying it. Default false — an
    // org opts in once the detection behavior is verified for their
    // pipelines; existing customer pipelines are never auto-modified by
    // default. Gates PackagesTable.tsx's auto-detect effect only — manual
    // add/edit/remove of package rows is unaffected either way.
    defaultValue: false,
    description:
      'Auto-adds packages detected from `import` statements in the BYO code editor. Off by default — silently modifying an existing pipeline’s package list based on a code edit is a real behavior change users should opt into per-org, not get by default.',
    type: 'feature' as const,
    category: 'ui' as const,
  },
} as const;
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;
export type FeatureFlagType = 'feature' | 'admin-only';
export type FeatureFlagCategory = 'ui' | 'analytics' | 'development' | 'experimental';

export interface FeatureFlagDefinition {
  key: string;
  defaultValue: boolean | any; // Allow any for JSON values
  description: string;
  type: FeatureFlagType;
  category: FeatureFlagCategory;
}

// DAG Modals specific types
export interface DagModalsConfig {
  credentials: boolean;
  source: {
    replication: boolean;
    spark: boolean;
    cdc: boolean;
    in_memory: boolean;
    elt: boolean;
    custom: boolean;
    streaming: boolean;
  };
  sink: {
    replication: boolean;
    spark: boolean;
    cdc: boolean;
    in_memory: boolean;
    elt: boolean;
    custom: boolean;
    streaming: boolean;
  };
  nexset: boolean;
}

// Debug Logger specific types
export interface DebugConfig {
  enabled: boolean;
  debug_level: number; // 0: no debug, 1: basic, 2: more info, 3: all debug info
  block_api_calls?: boolean; // When true, logs payload but prevents API call from firing
}

// Polling Configuration specific types
export interface PollingPresetConfig {
  intervalMs?: number | null; // Override interval for this preset
  pauseOnHidden?: boolean; // Override pauseOnHidden for this preset
  refreshOnVisible?: boolean; // Override refreshOnVisible for this preset
  pauseOnIdle?: boolean; // Override pauseOnIdle for this preset
  idleTimeoutMs?: number; // Override idleTimeoutMs for this preset
  enableAdaptiveBackoff?: boolean; // Override enableAdaptiveBackoff for this preset
  maxBackoffMultiplier?: number; // Override maxBackoffMultiplier for this preset
}

export interface RuntimeSelectionConfig {
  streaming: {
    rest: boolean;
    templatized: boolean;
    files: boolean;
    database: boolean;
  };
}

export interface CapsuleHelmDefaultsConfig {
  chartRepository?: string;
  version?: string;
  httpGatewayEndpointFqdn?: string;
  capsuleDockerImage?: string;
  alloyTelemetryApiKey?: string;
}

export interface PollingConfig {
  intervalMs: number | null; // null = use env variable, number = global override
  pauseOnHidden: boolean; // Pause polling when tab is hidden
  refreshOnVisible: boolean; // Immediate refresh when tab becomes visible
  pauseOnIdle: boolean; // Pause polling when user is idle
  idleTimeoutMs: number; // Idle timeout in milliseconds
  enableAdaptiveBackoff: boolean; // Enable exponential backoff when no changes detected
  maxBackoffMultiplier: number; // Max multiplier for backoff (e.g., 4 = 4x base interval)
  // Preset-specific overrides - allows fine-grained control per use case
  presets?: {
    DEFAULT?: PollingPresetConfig;
    FLOW_LOGS?: PollingPresetConfig;
    LIVE_SAMPLES?: PollingPresetConfig;
    PIPELINE_LIST?: PollingPresetConfig;
    UI_TIME_DISPLAY?: PollingPresetConfig;
    SCANNING?: PollingPresetConfig;
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get feature flag definition by key
 */
export function getFeatureFlag(key: FeatureFlagKey): FeatureFlagDefinition {
  return FEATURE_FLAGS[key];
}

/**
 * Get all feature flags of a specific type
 */
export function getFeatureFlagsByType(type: FeatureFlagType): FeatureFlagDefinition[] {
  return Object.values(FEATURE_FLAGS).filter((flag) => flag.type === type);
}

/**
 * Get all feature flags of a specific category
 */
export function getFeatureFlagsByCategory(category: FeatureFlagCategory): FeatureFlagDefinition[] {
  return Object.values(FEATURE_FLAGS).filter((flag) => flag.category === category);
}

/**
 * Get all feature flag keys
 */
export function getAllFeatureFlagKeys(): FeatureFlagKey[] {
  return Object.keys(FEATURE_FLAGS) as FeatureFlagKey[];
}

/**
 * Validate if a feature flag key exists
 */
export function isValidFeatureFlag(key: string): key is FeatureFlagKey {
  return key in FEATURE_FLAGS;
}

// ============================================================================
// FEATURE FLAG UTILITY FUNCTIONS
// ============================================================================

// Global reference to GrowthBook instance (set by GrowthBookProvider)
let globalGrowthBookInstance: any = null;

/**
 * Set the global GrowthBook instance (called by GrowthBookProvider)
 */
export const setGlobalGrowthBookInstance = (instance: any) => {
  globalGrowthBookInstance = instance;
};

/**
 * Get any feature flag value in non-React contexts
 * @param flagKey - The feature flag key from FEATURE_FLAGS
 * @returns The feature flag value or default value
 */
export const getFeatureFlagValue = <T>(flagKey: FeatureFlagKey): T => {
  const flag = FEATURE_FLAGS[flagKey];

  try {
    if (!globalGrowthBookInstance) {
      return flag.defaultValue as T;
    }

    const result = globalGrowthBookInstance.evalFeature(flag.key);

    if (
      result &&
      (result.source === 'experiment' ||
        result.source === 'force' ||
        result.source === 'defaultValue')
    ) {
      return result.value;
    }

    return flag.defaultValue as T;
  } catch (error) {
    console.warn(`Failed to evaluate feature flag ${flagKey}:`, error);
    return flag.defaultValue as T;
  }
};

// ============================================================================
// BACKWARD COMPATIBILITY EXPORTS
// ============================================================================

/**
 * Backward compatibility export for editModalsEnabled
 * This maintains compatibility with existing code while using the new feature flag system
 * Uses getFeatureFlagValue to get the actual flag value (with GrowthBook evaluation)
 */
export const editModalsEnabled = () => getFeatureFlagValue('UI_DAG_MODALS');

export const commonConnectors = () => {
  return getFeatureFlagValue('UI_COMMON_CONNECTORS');
};

export const checkIsPython3Enabled = () => getFeatureFlagValue('UI_ENABLE_PYTHON3');

/**
 * Get polling config in non-React contexts
 * Returns merged config from GrowthBook with env variable fallbacks
 */
export const getPollingConfig = (): PollingConfig => {
  const gbConfig = getFeatureFlagValue<Partial<PollingConfig>>('UI_POLLING_CONFIG');

  // Merge with defaults - GrowthBook values take precedence
  const defaultConfig = {
    intervalMs: null,
    pauseOnHidden: true,
    refreshOnVisible: true,
    pauseOnIdle: false,
    idleTimeoutMs: 300000,
    enableAdaptiveBackoff: false,
    maxBackoffMultiplier: 4,
  };

  return {
    ...defaultConfig,
    ...gbConfig,
  } as PollingConfig;
};

// ============================================================================
// RUNTIME SELECTION UTILITIES
// ============================================================================

export const RUNTIME_SELECTION_CATEGORY_MAP: Partial<
  Record<string, keyof RuntimeSelectionConfig['streaming']>
> = {
  rest: 'rest',
  templatized_api: 'templatized',
  templatized_api_multi: 'templatized',
  file: 'files',
  database: 'database',
};

export function resolveRuntimeEnabled(
  capsulesEnabled: boolean,
  runtimeConfig: RuntimeSelectionConfig | null | undefined,
  connectionCategory: string,
  flowType: string = 'streaming',
): boolean {
  if (!capsulesEnabled) return false;
  const flagKey = RUNTIME_SELECTION_CATEGORY_MAP[connectionCategory];
  const flowConfig = runtimeConfig?.[flowType as keyof RuntimeSelectionConfig];
  return flagKey ? (flowConfig?.[flagKey] ?? false) : false;
}

export function isSourceFormRuntimeSelectionEnabled(
  capsulesEnabled: boolean,
  runtimeConfig: RuntimeSelectionConfig | null | undefined,
  connectionCategory: string,
  flowType: string = 'streaming',
): boolean {
  if (flowType !== 'streaming') return false;
  return resolveRuntimeEnabled(capsulesEnabled, runtimeConfig, connectionCategory, 'streaming');
}

export function isRuntimeSelectionEnabled(flowType: string, connectionCategory: string): boolean {
  const capsulesEnabled = getFeatureFlagValue<boolean>('UI_ENABLE_CAPSULES_SETTINGS');
  const config = getFeatureFlagValue<RuntimeSelectionConfig>('UI_ENABLE_RUNTIME_SELECTION');
  return resolveRuntimeEnabled(capsulesEnabled, config, connectionCategory, flowType);
}

// ============================================================================
// FEATURE FLAG GROUPS
// ============================================================================

/**
 * Group feature flags by their usage patterns
 */
export const FEATURE_FLAG_GROUPS = {
  // UI Features
  UI_FEATURES: [
    FEATURE_FLAGS.UI_ENABLE_PYTHON3,
    FEATURE_FLAGS.UI_ENABLE_SPLITTER,
    FEATURE_FLAGS.UI_DISPLAY_FLOW_RUN_NOW,
    FEATURE_FLAGS.UI_DAG_MODALS,
    FEATURE_FLAGS.UI_ENABLE_API_SERVER_FLOW,
    FEATURE_FLAGS.UI_COMMON_CONNECTORS,
    FEATURE_FLAGS.UI_ENABLE_PRELOAD_WEB_WORKERS_FOR_MONACO,
    FEATURE_FLAGS.UI_POLLING_CONFIG,
  ],
  // Development Features
  DEVELOPMENT_FEATURES: [FEATURE_FLAGS.UI_ENABLE_DEBUGGER],
} as const;

// ============================================================================
// EXPORT TYPES FOR HOOKS
// ============================================================================

export type { FeatureFlagDefinition as FeatureFlag };
